import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";

interface FinalCTASectionProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
  backgroundImage?: string;
}

export default function FinalCTASection({
  title = "Some Moments Only Happen Once. Make Them Last Forever.",
  subtitle = "Every image we create is an heirloom — made to be held, displayed, and passed down. Begin with a free, personal consultation.",
  ctaLabel = "Create Your Custom Keepsake",
  ctaHref = "/services",
  backgroundImage = "https://public.readdy.ai/ai/img_res/b19a1ef8307d292d5df9abbf20fbc304.jpg",
}: FinalCTASectionProps) {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="relative flex items-center overflow-hidden" style={{ minHeight: "75vh" }}>

      {/* ── Background ─────────────────────────────────────── */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={backgroundImage}
          alt="Create your keepsake — Precious Keepsakes Photography San Antonio"
          className="w-full h-full object-cover object-center"
        />
        {/* Left-heavy dramatic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-pk-dark/95 via-pk-dark/72 to-pk-dark/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-pk-dark/60 via-transparent to-pk-dark/30" />
        {/* Gold vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(196,168,122,0.06) 0%, transparent 55%)" }} />
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-8 py-28 w-full">
        <div className="max-w-2xl">

          {/* Eyebrow */}
          <div className="reveal flex items-center gap-4 mb-7">
            <div className="w-10 h-px bg-pk-gold" />
            <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold">
              Precious Keepsakes Photography · San Antonio, TX
            </span>
          </div>

          {/* Headline */}
          <h2 className="reveal font-display font-light text-white leading-tight" style={{ fontSize: "clamp(2.4rem, 5vw, 5.2rem)" }}>
            {title}
          </h2>

          {/* Ornamental divider */}
          <div className="reveal delay-100 flex items-center gap-4 mt-7">
            <div className="w-12 h-px bg-pk-gold/40" />
            <div className="w-1.5 h-1.5 rounded-full bg-pk-gold/60" />
          </div>

          <p className="reveal delay-200 font-sans text-base text-white/60 mt-6 leading-8 max-w-lg">
            {subtitle}
          </p>

          {/* CTA buttons */}
          <div className="reveal delay-300 mt-12 flex flex-col sm:flex-row items-start gap-5">
            <Link
              to={ctaHref}
              className="bg-pk-gold text-white font-sans text-xs tracking-widest3 uppercase px-14 py-5 rounded-sm hover:bg-white hover:text-pk-charcoal transition-colors duration-400 whitespace-nowrap cursor-pointer"
            >
              {ctaLabel}
            </Link>
            <a
              href="tel:2108154698"
              className="flex items-center gap-3 font-sans text-sm text-white/55 hover:text-pk-gold transition-colors duration-300 cursor-pointer whitespace-nowrap group py-3"
            >
              <span className="w-10 h-10 flex items-center justify-center border border-white/15 rounded-full group-hover:border-pk-gold transition-colors duration-300">
                <i className="ri-phone-line text-sm" />
              </span>
              210-815-4698
            </a>
          </div>

          {/* Trust strip */}
          <div className="reveal delay-400 mt-10 flex flex-wrap items-center gap-x-5 gap-y-2">
            {[
              { icon: "ri-award-line", text: "15+ Years Experience" },
              { icon: "ri-star-fill", text: "5-Star Rated" },
              { icon: "ri-heart-line", text: "500+ Families" },
              { icon: "ri-shield-check-line", text: "Free Consultation" },
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-2 font-sans text-xs text-white/30">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`${item.icon} text-pk-gold/50`} style={{ fontSize: "11px" }} />
                </div>
                {item.text}
              </span>
            ))}
          </div>

        </div>
      </div>

    </section>
  );
}
